/**
 * Annotation Form Component
 * Modal form for creating annotations with scope selector and tag autocomplete
 */

import React, { useState, useRef, useEffect } from 'react';
import { useClipDataStore } from '../../stores/clipDataStore';
import type { Annotation } from '../../lib/api-clip-monitor';

type ScopeType = 'current_diaper' | 'time_selection' | 'measurement' | 'general';

interface AnnotationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingAnnotation?: Annotation | null;
}

export const AnnotationForm: React.FC<AnnotationFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingAnnotation,
}) => {
  const {
    annotations,
    selectedDiaperCounter,
    brushSelection,
    hoveredMeasurementTimestamp,
    addAnnotation,
    updateAnnotation,
  } = useClipDataStore();

  const [text, setText] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [scope, setScope] = useState<ScopeType>('current_diaper');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagSuggestionsOpen, setTagSuggestionsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const allTags = React.useMemo(() => {
    const set = new Set<string>();
    annotations.forEach((a) => a.tags.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [annotations]);

  const tagCandidates = tagsInput.trim()
    ? allTags.filter((t) => t.toLowerCase().includes(tagsInput.toLowerCase()))
    : allTags;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setTagSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (editingAnnotation) {
        setText(editingAnnotation.text);
        setTagsInput(editingAnnotation.tags.join(', '));
        
        if (editingAnnotation.measurementTimestamp) setScope('measurement');
        else if (editingAnnotation.timeRange) setScope('time_selection');
        else if (editingAnnotation.diaperCounter !== null) setScope('current_diaper');
        else setScope('general');
      } else {
        let initialScope: ScopeType = 'general';
        if (hoveredMeasurementTimestamp) initialScope = 'measurement';
        else if (
          brushSelection?.startTime &&
          brushSelection?.endTime &&
          brushSelection.startTime.getTime() !== brushSelection.endTime.getTime()
        ) {
          initialScope = 'time_selection';
        } else if (selectedDiaperCounter !== null) {
          initialScope = 'current_diaper';
        }
        setScope(initialScope);
        setText('');
        setTagsInput('');
      }
      setError(null);
    }
  }, [
    isOpen,
    editingAnnotation,
    hoveredMeasurementTimestamp,
    brushSelection?.startTime,
    brushSelection?.endTime,
    selectedDiaperCounter,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedText = text.trim();
    if (!trimmedText) {
      setError('Please enter a comment.');
      return;
    }

    const tags = tagsInput
      .split(/[,\s]+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    let diaperCounter: number | null = null;
    let timeRange: { start: string; end: string } | null = null;
    let measurementTimestamp: string | null = null;

    switch (scope) {
      case 'current_diaper':
        diaperCounter = selectedDiaperCounter;
        break;
      case 'time_selection':
        if (brushSelection?.startTime && brushSelection?.endTime) {
          timeRange = {
            start: brushSelection.startTime.toISOString(),
            end: brushSelection.endTime.toISOString(),
          };
        }
        break;
      case 'measurement':
        measurementTimestamp = hoveredMeasurementTimestamp || null;
        break;
      case 'general':
        break;
    }

    setIsSubmitting(true);
    try {
      const annotationData = {
        text: trimmedText,
        tags,
        diaperCounter: diaperCounter ?? undefined,
        timeRange: timeRange ?? undefined,
        measurementTimestamp: measurementTimestamp ?? undefined,
      };

      if (editingAnnotation) {
        await updateAnnotation(editingAnnotation.id, annotationData);
      } else {
        await addAnnotation(annotationData);
      }
      
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save annotation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTag = (tag: string) => {
    const current = tagsInput.trim();
    const parts = current ? current.split(/[,\s]+/).filter(Boolean) : [];
    if (!parts.includes(tag)) {
      parts.push(tag);
      setTagsInput(parts.join(', '));
    }
    setTagSuggestionsOpen(false);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            {editingAnnotation ? 'Edit Annotation' : 'Add Annotation'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Pooling toggles rapidly here - sensor contact issue?"
              rows={3}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
            <input
              ref={inputRef}
              type="text"
              value={tagsInput}
              onChange={(e) => {
                setTagsInput(e.target.value);
                setTagSuggestionsOpen(true);
              }}
              onFocus={() => setTagSuggestionsOpen(true)}
              placeholder="e.g. false-positive, pooling-flicker"
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {tagSuggestionsOpen && tagCandidates.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-32 overflow-y-auto"
              >
                {tagCandidates.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => addTag(tag)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scope</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  value="current_diaper"
                  checked={scope === 'current_diaper'}
                  onChange={() => setScope('current_diaper')}
                  disabled={selectedDiaperCounter === null}
                  className="text-blue-600"
                />
                <span className="text-sm">
                  Current diaper
                  {selectedDiaperCounter !== null && ` (#${selectedDiaperCounter})`}
                  {selectedDiaperCounter === null && ' (no diaper selected)'}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  value="time_selection"
                  checked={scope === 'time_selection'}
                  onChange={() => setScope('time_selection')}
                  disabled={
                    !brushSelection?.startTime ||
                    !brushSelection?.endTime ||
                    brushSelection.startTime.getTime() === brushSelection.endTime.getTime()
                  }
                  className="text-blue-600"
                />
                <span className="text-sm">
                  Current time selection
                  {brushSelection?.startTime &&
                    brushSelection?.endTime &&
                    brushSelection.startTime.getTime() !== brushSelection.endTime.getTime() &&
                    ' (brush)'}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  value="measurement"
                  checked={scope === 'measurement'}
                  onChange={() => setScope('measurement')}
                  disabled={!hoveredMeasurementTimestamp}
                  className="text-blue-600"
                />
                <span className="text-sm">
                  Specific measurement
                  {hoveredMeasurementTimestamp && ' (hovered)'}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  value="general"
                  checked={scope === 'general'}
                  onChange={() => setScope('general')}
                  className="text-blue-600"
                />
                <span className="text-sm">General note</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (editingAnnotation ? 'Save Changes' : 'Add Annotation')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
